// School name normalization + conservative fuzzy matching.
// Goal: merge names that are clearly the same school (casing, abbreviations,
// punctuation, "of"/"the" noise). If two names are close but not confidently
// identical, keep them separate rather than merging the wrong schools.

const ABBREVIATIONS: Record<string, string> = {
  u: "university",
  uni: "university",
  univ: "university",
  "univ.": "university",
  coll: "college",
  "coll.": "college",
  clg: "college",
  st: "saint",
  "st.": "saint",
  inst: "institute",
  tech: "technology",
  "&": "and",
};

const SMALL_WORDS = new Set(["of", "the", "at", "and", "in", "for"]);

/** Canonical display form: trimmed, single-spaced, Title Case with small words lowered. */
export function canonicalizeSchoolName(raw: string): string {
  const cleaned = raw
    .replace(/\s+/g, " ")
    .replace(/[’']/g, "'")
    .trim();
  if (!cleaned) return "";

  const words = cleaned.split(" ").map((w) => {
    const key = w.toLowerCase();
    const expanded = ABBREVIATIONS[key] ?? key;
    return expanded;
  });

  return words
    .map((w, i) => {
      if (i > 0 && SMALL_WORDS.has(w)) return w;
      if (/^[A-Z]{2,}$/.test(w)) return w; // keep acronyms typed in caps
      return w
        .split("-")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join("-");
    })
    .join(" ");
}

/** Aggressive key used only for comparison, never for display. */
export function schoolMatchKey(raw: string): string {
  return canonicalizeSchoolName(raw)
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, "")
    .split(" ")
    .filter((w) => w && !SMALL_WORDS.has(w))
    .sort()
    .join(" ");
}

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(
        prev[j] + 1,
        cur[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
    prev = cur;
  }
  return prev[n];
}

function similarity(a: string, b: string): number {
  const max = Math.max(a.length, b.length);
  if (!max) return 1;
  return 1 - levenshtein(a, b) / max;
}

export interface SchoolMatch {
  /** Name to store. */
  name: string;
  /** True when we merged the input into an existing school. */
  merged: boolean;
}

/**
 * Match an input school against known school names.
 * - Identical comparison keys, or a known name that fully contains the input
 *   as a prefix ("Duquesne" -> "Duquesne University"), merge automatically.
 * - Near-identical spellings (>= 0.92 similarity) merge (typos).
 * - Anything less confident stays its own school.
 */
export function matchSchoolName(raw: string, knownNames: string[]): SchoolMatch {
  const name = canonicalizeSchoolName(raw);
  if (!name) return { name: "", merged: false };

  const key = schoolMatchKey(name);
  const inputWords = key.split(" ");

  let best: { name: string; score: number } | null = null;

  for (const known of knownNames) {
    const knownCanonical = canonicalizeSchoolName(known);
    const knownKey = schoolMatchKey(knownCanonical);
    if (!knownKey) continue;

    if (knownKey === key) return { name: knownCanonical, merged: true };

    // "Duquesne" vs "Duquesne University": every input word present and the
    // only extra words are generic institution words.
    const knownWords = knownKey.split(" ");
    const generic = new Set(["university", "college", "school", "institute", "pharmacy", "medicine", "nursing"]);
    const missing = inputWords.filter((w) => !knownWords.includes(w));
    const extra = knownWords.filter((w) => !inputWords.includes(w));
    if (missing.length === 0 && extra.length > 0 && extra.every((w) => generic.has(w))) {
      return { name: knownCanonical, merged: true };
    }

    const score = similarity(key, knownKey);
    if (!best || score > best.score) best = { name: knownCanonical, score };
  }

  if (best && best.score >= 0.92) return { name: best.name, merged: true };
  return { name, merged: false };
}
